import React from 'react';
import { bindPopover } from 'material-ui-popup-state/hooks';

import { usePaginationParams } from '@occc-app/hooks/usePaginationParams';
import { useGetMembershipVouchers } from '@occc-app/dataAccessLayer/membership/queries';
import { Sort_Order, VoucherFilter, VoucherSort } from '@occc/graphql';
import { Popover } from '@occc/ui-design-system';

import { getDateRange, headers, makeFields } from './utils';
import { VouchersFilterForm } from './VouchersFilterForm';
import {
  useCreatedAtEndFilter,
  useCreatedAtRangeFilter,
  useCreatedAtStartFilter,
  useExpireAtEndFilter,
  useExpireAtRangeFilter,
  useExpireAtStartFilter,
  useSortingParams,
  useSourceTypeFilter,
  useStateFilter,
} from './hooks';
import { DateRange, VouchersFilterFormProps, VouchersFilterFormValues } from './types';

import { VOUCHERS_AFTER_PARAM, VOUCHERS_BEFORE_PARAM } from '../../../constants';
import VouchersTable from '../components/VouchersTable';

type VouchersContainerProps = {
  memberId: string;
  searchText?: string;
  popoverProps: ReturnType<typeof bindPopover>;
};

export const VouchersContainer = ({
  memberId,
  searchText,
  popoverProps,
}: VouchersContainerProps) => {
  const [sourceType, setSourceType] = useSourceTypeFilter();
  const [voucherState, setVoucherState] = useStateFilter();
  const [createdAtRange, setCreatedAtRange] = useCreatedAtRangeFilter();
  const [expireAtRange, setExpireAtRange] = useExpireAtRangeFilter();
  const [createdAtStart, setCreatedAtStart] = useCreatedAtStartFilter();
  const [createdAtEnd, setCreatedAtEnd] = useCreatedAtEndFilter();
  const [expireAtStart, setExpireAtStart] = useExpireAtStartFilter();
  const [expireAtEnd, setExpireAtEnd] = useExpireAtEndFilter();
  const [sortingParams] = useSortingParams();

  const [paginationInfo, onPageChange] = usePaginationParams({
    beforeParamName: VOUCHERS_BEFORE_PARAM,
    afterParamName: VOUCHERS_AFTER_PARAM,
  });

  const filter: VoucherFilter = React.useMemo(() => {
    return {
      sourceType: sourceType !== 'any' ? sourceType : undefined,
      state: voucherState !== 'any' ? voucherState : undefined,
      createdAtMin: createdAtStart?.toISOString() || undefined,
      createdAtMax: createdAtEnd?.toISOString() || undefined,
      expiredAtMin: expireAtStart?.toISOString() || undefined,
      expiredAtMax: expireAtEnd?.toISOString() || undefined,
      search: searchText || null,
    };
  }, [
    sourceType,
    voucherState,
    createdAtStart,
    createdAtEnd,
    expireAtStart,
    expireAtEnd,
    searchText,
  ]);

  const sort: VoucherSort | undefined = React.useMemo(() => {
    if (!sortingParams) return undefined;
    return {
      field: sortingParams.field,
      direction: sortingParams.direction === 'desc' ? Sort_Order.Desc : Sort_Order.Asc,
    };
  }, [sortingParams]);

  const { data, loading, error, paginationProps } = useGetMembershipVouchers({
    membershipId: memberId,
    filter,
    sort,
    paginationInfo,
    onPageChange,
  });

  const onSubmit: VouchersFilterFormProps['onSubmit'] = ({
    sourceType,
    state,
    createdAt,
    expireAt,
  }) => {
    setSourceType(sourceType);
    setVoucherState(state);
    setCreatedAtRange(createdAt.range);
    setExpireAtRange(expireAt.range);

    if (createdAt.range === DateRange.Custom) {
      setCreatedAtStart(createdAt.start);
      setCreatedAtEnd(createdAt.end);
    } else if (createdAt.range === 'any') {
      setCreatedAtStart(null);
      setCreatedAtEnd(null);
    } else {
      const range = getDateRange(createdAt.range);
      setCreatedAtStart(range.start);
      setCreatedAtEnd(range.end);
    }

    if (expireAt.range === DateRange.Custom) {
      setExpireAtStart(expireAt.start);
      setExpireAtEnd(expireAt.end);
    } else if (expireAt.range === 'any') {
      setExpireAtStart(null);
      setExpireAtEnd(null);
    } else {
      const range = getDateRange(expireAt.range);
      setCreatedAtStart(range.start);
      setCreatedAtEnd(range.end);
    }

    popoverProps.onClose();
  };

  const onReset = () => {
    setSourceType('any');
    setVoucherState('any');
    setCreatedAtStart(null);
    setCreatedAtEnd(null);
    setCreatedAtRange('any');
    setExpireAtStart(null);
    setExpireAtEnd(null);
    setExpireAtRange('any');
    popoverProps.onClose();
  };

  const filterInitialValues: VouchersFilterFormValues = React.useMemo(() => {
    return {
      sourceType,
      state: voucherState,
      createdAt: { range: createdAtRange, start: createdAtStart, end: createdAtEnd },
      expireAt: { range: expireAtRange, start: expireAtStart, end: expireAtEnd },
    };
  }, [
    createdAtEnd,
    createdAtRange,
    createdAtStart,
    expireAtEnd,
    expireAtRange,
    expireAtStart,
    sourceType,
    voucherState,
  ]);

  return (
    <>
      <VouchersTable
        vouchers={data}
        error={error}
        loading={loading}
        paginationProps={paginationProps}
        headers={headers}
        makeFields={makeFields}
      />

      <Popover
        {...popoverProps}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        keepMounted
        realignWhenContentChanged
      >
        <VouchersFilterForm
          onSubmit={onSubmit}
          onReset={onReset}
          initialValues={filterInitialValues}
        />
      </Popover>
    </>
  );
};
